/**
 * Comment List — threaded comments with replies (1 level deep).
 * Flat design with proper spacing and interaction.
 */

import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Heart, CornerDownRight } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { uz } from 'date-fns/locale'
import type { CommentWithAuthor } from '@turnifly/shared/api'

interface CommentListProps {
  comments: CommentWithAuthor[]
  onReply: (commentId: string, authorName: string) => void
  onLikeComment: (commentId: string, isLiked: boolean) => void
}

export const CommentList = memo(function CommentList({
  comments, onReply, onLikeComment,
}: CommentListProps): React.ReactElement {
  const { i18n } = useTranslation()
  const isUz = i18n.language === 'uz'

  const { roots, repliesMap } = useMemo(() => {
    const rootComments: CommentWithAuthor[] = []
    const replyMap = new Map<string, CommentWithAuthor[]>()

    for (const c of comments) {
      if (c.parent_id) {
        const arr = replyMap.get(c.parent_id) ?? []
        arr.push(c)
        replyMap.set(c.parent_id, arr)
      } else {
        rootComments.push(c)
      }
    }
    return { roots: rootComments, repliesMap: replyMap }
  }, [comments])

  return (
    <div className="divide-y divide-white/[0.04]">
      {roots.map((comment) => (
        <div key={comment.id}>
          <CommentRow
            comment={comment}
            onReply={onReply}
            onLike={onLikeComment}
            isUz={isUz}
          />
          {repliesMap.has(comment.id) && (
            <div className="ml-12 border-l-2 border-white/[0.06]">
              {repliesMap.get(comment.id)!.map((reply) => (
                <CommentRow
                  key={reply.id}
                  comment={reply}
                  onReply={onReply}
                  onLike={onLikeComment}
                  isReply
                  isUz={isUz}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
})

function CommentRow({ comment, onReply, onLike, isReply, isUz }: {
  comment: CommentWithAuthor
  onReply: (id: string, name: string) => void
  onLike: (id: string, isLiked: boolean) => void
  isReply?: boolean
  isUz: boolean
}): React.ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const timeAgo = formatDistanceToNow(new Date(comment.created_at), {
    addSuffix: false,
    locale: isUz ? uz : undefined,
  })

  return (
    <div className={`flex gap-2.5 py-3 ${isReply ? 'pl-3 pr-4' : 'px-4'}`}>
      <button
        onClick={() => navigate(`/profile/${comment.user_id}`)}
        className="shrink-0 w-8 h-8 rounded-full overflow-hidden bg-white/[0.06] tap-highlight-transparent"
      >
        {comment.author_avatar ? (
          <img src={comment.author_avatar} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[11px] text-text-hint/50 font-semibold">
            {(comment.author_name ?? '?')[0]}
          </div>
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/profile/${comment.user_id}`)}
            className="text-[13px] font-semibold text-text tap-highlight-transparent hover:underline"
          >
            {comment.author_name}
          </button>
          <span className="text-[10px] text-text-hint/25">{timeAgo}</span>
        </div>
        <p className="text-[13px] text-text/80 leading-relaxed mt-0.5 break-words">{comment.text}</p>

        {/* Reply button */}
        <button
          onClick={() => onReply(comment.id, comment.author_name)}
          className="flex items-center gap-1 mt-1.5 text-[11px] text-text-hint/35 hover:text-text-hint/60 tap-highlight-transparent transition-colors"
        >
          <CornerDownRight size={10} />
          {t('feed.comments.reply')}
        </button>
      </div>

      {/* Like button */}
      <button
        onClick={() => onLike(comment.id, comment.is_liked_by_me)}
        className="shrink-0 flex flex-col items-center gap-0.5 pt-1.5 tap-highlight-transparent"
      >
        <Heart
          size={14}
          className={`transition-colors ${
            comment.is_liked_by_me
              ? 'text-red-500 fill-red-500'
              : 'text-text-hint/15 hover:text-text-hint/30'
          }`}
        />
        {comment.likes_count > 0 && (
          <span className={`text-[9px] tabular-nums ${
            comment.is_liked_by_me ? 'text-red-500/70' : 'text-text-hint/20'
          }`}>
            {comment.likes_count}
          </span>
        )}
      </button>
    </div>
  )
}
